'use client';
import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppDownloadModal from '@/components/AppDownloadModal';
import CampaignApplyModal from '@/components/CampaignApplyModal';
import ProductFilters from './ProductFilters';
import CampaignGrid from './CampaignGrid';
import Icon from '@/components/ui/AppIcon';

export type FilterState = {
  category: string;
  minReward: number;
  maxReward: number;
  marketplace: string;
  minRating: number;
  brand: string;
};

const initialFilters: FilterState = {
  category: 'All',
  minReward: 0,
  maxReward: 5000,
  marketplace: 'All',
  minRating: 0,
  brand: '',
};

export default function ProductsClient() {
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<{
    id: string;
    brand: string;
    product: string;
    category: string;
    reward_amount: number;
  } | null>(null);
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleApplyClick = (campaign: {
    id: string;
    brand: string;
    product: string;
    category: string;
    reward_amount: number;
  }) => {
    setSelectedCampaign(campaign);
    setApplyModalOpen(true);
  };

  return (
    <>
      <Header onDownloadClick={() => setDownloadModalOpen(true)} />
      <main className="pt-16 min-h-screen bg-white">
        {/* Page Header */}
        <div className="bg-white border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
            <span className="pill-badge mb-3">Products for Review</span>
            <h1 className="font-heading font-bold text-foreground mb-2 mt-3" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)' }}>
              Pick Products to Review
            </h1>
            <p className="text-muted-foreground mb-6">
              Browse products submitted by brands for review and testing. Pick what interests you, review it, and earn via UPI.
            </p>
            {/* Search Bar */}
            <div className="relative max-w-xl">
              <Icon name="MagnifyingGlassIcon" size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by product name, brand, or category…"
                className="w-full pl-11 pr-10 py-3 rounded-2xl border border-border bg-[#F7F8FA] text-sm text-foreground placeholder-muted-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg hover:bg-muted text-muted-foreground"
                >
                  <Icon name="XMarkIcon" size={14} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex gap-8">
            {/* Sidebar Filters */}
            <aside className={`
              fixed inset-y-0 left-0 z-40 w-72 bg-white shadow-xl transform transition-transform duration-300 md:relative md:inset-auto md:z-auto md:w-64 md:shadow-none md:transform-none md:block
              ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
            `}>
              <ProductFilters
                filters={filters}
                onFilterChange={setFilters}
                onClose={() => setSidebarOpen(false)}
              />
            </aside>

            {/* Overlay for mobile sidebar */}
            {sidebarOpen && (
              <div
                className="fixed inset-0 z-30 bg-black/40 md:hidden"
                onClick={() => setSidebarOpen(false)}
              />
            )}

            {/* Main content */}
            <div className="flex-1 min-w-0">
              {/* Mobile filter button */}
              <button
                className="md:hidden mb-4 flex items-center gap-2 text-sm font-semibold text-foreground bg-white border border-border rounded-xl px-4 py-2"
                onClick={() => setSidebarOpen(true)}
              >
                <Icon name="AdjustmentsHorizontalIcon" size={16} className="text-muted-foreground" />
                <span>Filters</span>
              </button>
              <CampaignGrid filters={filters} searchQuery={searchQuery} onApplyClick={handleApplyClick} />
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <AppDownloadModal isOpen={downloadModalOpen} onClose={() => setDownloadModalOpen(false)} />
      <CampaignApplyModal
        isOpen={applyModalOpen}
        onClose={() => setApplyModalOpen(false)}
        campaign={selectedCampaign}
      />
    </>
  );
}